import { z } from 'zod';

import { isTruthy } from '../../../utils.js';
import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';
import { BlockValueNumberSchema } from '../base/blockValueNumberSchema.js';

export enum FileConversionFormat {
  Base64 = 'Base64',
  Pdf = 'Pdf',
  Base64ToFile = 'Base64ToFile',
}

export enum FileConversionOperation {
  Convert = 'Convert',
  SplitPdf = 'SplitPdf',
  CombinePdf = 'CombinePdf',
}

export enum SplitMode {
  Range = 'Range',
  EveryXPages = 'EveryXPages',
}

const ConvertConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.Convert),
  fileReference: z.string().optional(),
  base64Payload: z.string().optional(),
  format: z.nativeEnum(FileConversionFormat).default(FileConversionFormat.Base64),
});

const SplitPdfConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.SplitPdf),
  fileReference: z.string().min(1),
  splitMode: z.nativeEnum(SplitMode).default(SplitMode.Range),
  pageRanges: z.string().optional(),
  pageCount: BlockValueNumberSchema({
    numberValidationSchema: z.number().int().positive(),
  }).optional(),
});

const CombinePdfConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.CombinePdf),
  fileReferences: z.string().min(1),
});

export type ConvertConfig = z.infer<typeof ConvertConfigSchema>;
export type SplitPdfConfig = z.infer<typeof SplitPdfConfigSchema>;
export type CombinePdfConfig = z.infer<typeof CombinePdfConfigSchema>;

type LegacyConvertConfig = Omit<ConvertConfig, 'operation'>;
type FileConversionConfigInput =
  | ConvertConfig
  | SplitPdfConfig
  | CombinePdfConfig
  | LegacyConvertConfig;

function withDefaultOperation(
  value: FileConversionConfigInput
): ConvertConfig | SplitPdfConfig | CombinePdfConfig {
  if ('operation' in value) {
    return value;
  }
  return { ...value, operation: FileConversionOperation.Convert };
}

export const FileConversionBlockConfigSchema = z
  .preprocess(
    (value) => withDefaultOperation(value as FileConversionConfigInput),
    z.discriminatedUnion('operation', [
      ConvertConfigSchema,
      SplitPdfConfigSchema,
      CombinePdfConfigSchema,
    ])
  )
  .superRefine((config, ctx) => {
    if (config.operation === FileConversionOperation.Convert) {
      if (config.format === FileConversionFormat.Base64ToFile) {
        if (!isTruthy(config?.base64Payload)) {
          ctx.addIssue({ code: 'custom', message: 'Please provide a Base64 payload to convert.' });
        }
        if (isTruthy(config?.fileReference)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Remove the file reference when converting from Base64.',
          });
        }
      } else {
        if (!isTruthy(config?.fileReference)) {
          ctx.addIssue({ code: 'custom', message: 'Please provide a file reference to convert.' });
        }
        if (isTruthy(config?.base64Payload)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Remove the Base64 payload when converting a file.',
          });
        }
      }
    }

    if (config.operation === FileConversionOperation.SplitPdf) {
      if (config.splitMode === SplitMode.Range && !isTruthy(config?.pageRanges)) {
        ctx.addIssue({ code: 'custom', message: 'Please provide the page ranges to split.' });
      }
      if (config.splitMode === SplitMode.EveryXPages && !isTruthy(config?.pageCount)) {
        ctx.addIssue({ code: 'custom', message: 'Please provide the number of pages per file.' });
      }
    }

    if (config.operation === FileConversionOperation.CombinePdf) {
      if (!isTruthy(config?.fileReferences)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide file references to combine.',
        });
      }
    }
  });

export const FileConversionBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.FileConversion),
  config: FileConversionBlockConfigSchema,
});

export type FileConversionBlock = z.infer<typeof FileConversionBlockSchema>;
